import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type {
  PatientContext,
  PraktijkProfiel,
  SessieAntwoord,
  SipInstellingen,
} from '@/lib/api-types';
import { DEFAULT_PRACTICE_SETTINGS, PracticeSettings } from '@/types/practice-settings';

/**
 * De sessie van een zorgverlener: één tokenruil, daarna een cookie.
 *
 * Wat hier verdwijnt is een patroon dat door de hele frontend liep. Elke
 * edge-function-aanroep stuurde `token`, `user_id` en `app_token` mee in zijn body,
 * dus die drie werden als props doorgegeven aan elk component dat iets met de server
 * deed -- en ze stonden in de URL van /settings en /handleiding, en dus in de
 * browsergeschiedenis. Nu wisselt deze hook ze één keer in voor een HttpOnly-cookie,
 * en weet de rest van de applicatie niet meer dat er tokens bestaan.
 *
 * De vorige versie deed de tokenruil met een handmatige `fetch` in plaats van via de
 * Supabase-client, met een comment erbij: die client wachtte op `getSession()` en kon
 * daarbij een synchrone tokenvernieuwing uitlokken, wat 200 tot 800 ms aan het
 * kritieke "Toegang controleren"-pad toevoegde. Dat probleem bestaat niet meer -- er
 * is geen client die eerst iets anders moet doen.
 */

export type SessieStatus =
  | { fase: 'bezig' }
  /** Er is een geldige sessie. */
  | { fase: 'ok' }
  /** Er is geen token in de URL, of het token is ongeldig. */
  | { fase: 'geen-toegang'; melding: string }
  /** Wel een token en een gebruiker, maar geen app_token: vraag erom. */
  | { fase: 'app-token-nodig' }
  /** De praktijk of gebruiker is gedeactiveerd. */
  | { fase: 'geblokkeerd'; melding: string };

/**
 * De launch-code uit het fragment: `https://…/#c=<code>`.
 *
 * Een fragment en geen query-parameter, en dat is de kern van de nieuwe manier van
 * aanroepen: de browser stuurt een fragment niet mee naar de server, dus de code
 * staat in geen enkele access log en in geen enkele request-telemetrie. Zie
 * apps/api/src/lib/launch.ts.
 */
function launchCodeUitHash(): string | null {
  const hash = typeof window === 'undefined' ? '' : window.location.hash;
  if (!hash.startsWith('#')) return null;

  const code = new URLSearchParams(hash.slice(1)).get('c');
  return code && code.trim() !== '' ? code : null;
}

/**
 * Haalt querystring en fragment uit de adresbalk, zonder te navigeren.
 *
 * Wat er in de URL stond heeft zijn werk gedaan op het moment dat de sessie in het
 * cookie zit. Laten staan betekent: in de browsergeschiedenis, in een screenshot,
 * in een schermdeling. Een herlaadactie komt daarna op GET /me uit, en dat geeft
 * hetzelfde antwoord.
 */
function schoonUrlOp(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname);
  } catch {
    /* geen history-API; dan blijft de URL staan en werkt de rest gewoon */
  }
}

// --- De patiëntcontext -------------------------------------------------------

/**
 * Waar de patiëntcontext van de huidige launch bewaard blijft: sessionStorage, dus
 * per venster en weg zodra het venster sluit.
 *
 * Nodig omdat het opnamescherm deze hook niet overleeft. Wie naar /settings of
 * /handleiding gaat en terugkomt, of het scherm herlaadt, komt op GET /me uit -- en
 * die kent de patiënt niet, want het cookie draagt alleen de praktijk en de
 * gebruiker. Zonder deze opslag zou het geslacht halverwege een consult verdwijnen.
 *
 * Elke nieuwe launch overschrijft de waarde, ook een launch zonder patiënt: een
 * telefoonnummer van de vorige patiënt hoort niet in het belveld te blijven staan.
 */
const PATIENT_OPSLAG = 'mhvts_patient';

function leesBewaardePatient(): PatientContext | null {
  try {
    const ruw = window.sessionStorage.getItem(PATIENT_OPSLAG);
    if (!ruw) return null;
    const p = JSON.parse(ruw) as Partial<PatientContext>;
    return patientContext(p.gender ?? null, p.phone ?? null);
  } catch {
    return null;
  }
}

function bewaarPatient(patient: PatientContext | null): void {
  try {
    if (patient) {
      window.sessionStorage.setItem(PATIENT_OPSLAG, JSON.stringify(patient));
    } else {
      window.sessionStorage.removeItem(PATIENT_OPSLAG);
    }
  } catch {
    /* geen opslag (privévenster, geblokkeerd): dan geldt de context tot een herlaadactie */
  }
}

/** Dezelfde regels als de API, in apps/api/src/lib/patient.ts. */
function isTelefoonnummer(s: string): boolean {
  return s.length <= 32 && /^\+?[0-9 ().-]+$/.test(s) && (s.match(/[0-9]/g)?.length ?? 0) >= 6;
}

/**
 * Een patiëntcontext uit losse waarden, of null als er niets bruikbaars in zit.
 *
 * Een ongeldige waarde wordt genegeerd en niet geweigerd. Bij de oude weg staat hij in
 * de querystring, en er is geen manier om het HIS daarover terug te melden; het hele
 * scherm blokkeren om een verkeerd geslacht helpt de zorgverlener niet.
 */
function patientContext(
  geslacht: string | null | undefined,
  telefoon: string | null | undefined,
): PatientContext | null {
  const g = geslacht?.trim().toUpperCase();
  const gender = g === 'M' || g === 'V' || g === 'X' ? g : null;

  const t = telefoon?.trim();
  const phone = t && isTelefoonnummer(t) ? t : null;

  if (geslacht && !gender) console.warn('patient_gender genegeerd: verwacht M, V of X.');
  if (telefoon && !phone) console.warn('patient_phone genegeerd: geen geldig telefoonnummer.');

  return gender || phone ? { gender, phone } : null;
}

/** Eén plek waar een fout van de tokenruil een status wordt. */
function naarStatus(fout: unknown): SessieStatus {
  if (fout instanceof ApiFout) {
    // 403 betekent dat de tokens klopten maar de praktijk of gebruiker
    // gedeactiveerd is. Dat is iets anders dan een ongeldig token, en de
    // gebruiker heeft er een andere boodschap aan.
    if (fout.status === 403) return { fase: 'geblokkeerd', melding: fout.message };
    return { fase: 'geen-toegang', melding: fout.message };
  }
  return {
    fase: 'geen-toegang',
    melding: 'Geen verbinding met de server. Probeer het opnieuw.',
  };
}

export interface PracticeSession {
  status: SessieStatus;
  practice: PraktijkProfiel | null;
  practiceUserId: string | null;
  sip: SipInstellingen | null;
  settings: PracticeSettings;
  /** True als de tokenruil deze praktijk zojuist heeft aangemaakt. */
  practiceIsNew: boolean;
  /**
   * True zolang de praktijknaam nog niet is ingevuld. Anders dan practiceIsNew
   * blijft dit waar na een herlaadactie, zodat de wizard de naam blijft vragen.
   */
  practiceNeedsName: boolean;
  /** True als deze gebruiker nog geen profiel heeft ingevuld. */
  needsOnboarding: boolean;
  /** Geslacht en telefoonnummer van de patiënt, als het HIS die meestuurde. */
  patient: PatientContext | null;
  /** Handmatig een app-token proberen, wanneer het HIS er geen meestuurde. */
  probeerAppToken: (appToken: string) => Promise<void>;
  /** Na een wijziging in de eigen voorkeuren: de gegevens opnieuw ophalen. */
  herlaad: () => Promise<void>;
  /** Lokaal bijwerken zonder een rondje naar de server, na een geslaagde PATCH. */
  pasSettingsAan: (deel: Partial<PracticeSettings>) => void;
  markeerOnboardingKlaar: () => void;
  markeerPraktijkNaam: (naam: string) => void;
}

/**
 * Zet het antwoord van de API om in de instellingen die het opnamescherm gebruikt.
 *
 * De gebruikersvoorkeur wint boven de praktijkinstelling; dat was ook de regel in de
 * bron, maar daar stond hij uitgeschreven in SoepPage.tsx als veertig regels
 * `??`-ketens. Hier staat hij één keer.
 */
function naarSettings(antwoord: SessieAntwoord): PracticeSettings {
  const p = antwoord.practice;
  const u = antwoord.user;
  const sip = antwoord.sip;

  /** Leeg telt als niet gezet, net als op de server. */
  const tekst = (van: string | null | undefined, terug: string): string =>
    van !== null && van !== undefined && van !== '' ? van : terug;

  return {
    auto_translate: u?.autoTranslate ?? p.autoTranslate,
    auto_suggest_icpc: u?.autoSuggestIcpc ?? p.autoSuggestIcpc,
    doctor_phrase: tekst(u?.doctorPhrase, p.doctorPhrase),
    read_trigger_phrase: tekst(u?.readTriggerPhrase, p.readTriggerPhrase),
    provider_role: u?.providerRole ?? '',
    provider_specializations: u?.providerSpecializations ?? [],
    report_verbosity: u?.reportVerbosity ?? 'gemiddeld',
    personal_profile: u?.personalProfile ?? '',
    force_single_soep: u?.forceSingleSoep ?? false,

    local_api_url: tekst(p.localApiUrl, DEFAULT_PRACTICE_SETTINGS.local_api_url),
    default_languages: p.defaultLanguages.length > 0 ? p.defaultLanguages : ['nl-NL'],
    calling_enabled: p.callingEnabled,
    patient_share_enabled: p.patientShareEnabled,
    live_transcript_enabled: p.liveTranscriptEnabled,

    // SIP komt al samengevoegd van de server; 8089 is de standaardpoort en hoort
    // niet in de URL terecht te komen.
    sip_host: sip?.host ?? '',
    sip_ws_port: sip?.wsPort === '8089' ? '' : (sip?.wsPort ?? ''),
    sip_ws_path: tekst(sip?.wsPath, '/ws'),
    sip_extension: sip?.extension ?? '',
    sip_password: sip?.password ?? '',
    sip_display_name: sip?.displayName ?? '',
    sip_stun_server: sip?.stunServer ?? '',
    sip_dial_prefix: sip?.dialPrefix ?? '',
  };
}

export function usePracticeSession(params: URLSearchParams): PracticeSession {
  const [status, setStatus] = useState<SessieStatus>({ fase: 'bezig' });
  const [practice, setPractice] = useState<PraktijkProfiel | null>(null);
  const [practiceUserId, setPracticeUserId] = useState<string | null>(null);
  const [sip, setSip] = useState<SipInstellingen | null>(null);
  const [settings, setSettings] = useState<PracticeSettings>(DEFAULT_PRACTICE_SETTINGS);
  const [practiceIsNew, setPracticeIsNew] = useState(false);
  const [practiceNeedsName, setPracticeNeedsName] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  /**
   * De tokens die het HIS meestuurde, alleen tijdens de eerste uitwisseling nodig.
   * In een ref en niet in state: ze horen geen render te veroorzaken, en ze horen
   * ook niet in een dependency-lijst die de uitwisseling opnieuw kan aftrappen.
   */
  const accessToken = params.get('token');
  const externalUserId = params.get('user_id');
  const appTokenUitUrl = params.get('app_token');
  const patientGeslacht = params.get('patient_gender');
  const patientTelefoon = params.get('patient_phone');
  const alGewisseld = useRef(false);

  /**
   * De launch-code, één keer gelezen bij het opzetten van de hook.
   *
   * Moet vóór het opschonen van de URL gelezen zijn, en mag daarna niet opnieuw
   * uit de adresbalk komen -- daar staat hij dan niet meer. Vandaar de lazy
   * initializer en niet een aanroep in de effect-body.
   */
  const [launchCode] = useState<string | null>(launchCodeUitHash);

  /**
   * De patiënt. Bij een nieuwe launch (code of tokens in de URL) gezet zodra de ruil
   * slaagt; zonder launch -- terug van /settings, of herladen -- uit sessionStorage.
   */
  const [patient, setPatient] = useState<PatientContext | null>(() =>
    launchCodeUitHash() || accessToken ? null : leesBewaardePatient(),
  );

  const zetPatient = useCallback((p: PatientContext | null) => {
    setPatient(p);
    bewaarPatient(p);
  }, []);

  const verwerk = useCallback((antwoord: SessieAntwoord) => {
    setPractice(antwoord.practice);
    setPracticeUserId(antwoord.user?.id ?? null);
    setSip(antwoord.sip);
    setSettings(naarSettings(antwoord));
    setPracticeIsNew(antwoord.practiceIsNew);
    setPracticeNeedsName(antwoord.practiceNeedsName);
    // Een nieuwe gebruiker, of een die de wizard nooit afmaakte: zonder
    // display_name is het profiel niet ingevuld.
    setNeedsOnboarding(antwoord.userIsNew || (antwoord.user !== null && !antwoord.user.displayName));
    setStatus({ fase: 'ok' });

    // Het HIS kan een venstergrootte opgeven. Alleen zinvol in een popup.
    const { recordWindowWidth: w, recordWindowHeight: h } = antwoord.practice;
    if (w && h) {
      try {
        window.resizeTo(w, h);
      } catch {
        /* geen popup */
      }
    }
  }, []);

  const wissel = useCallback(
    async (appToken: string) => {
      if (!accessToken) {
        setStatus({ fase: 'geen-toegang', melding: 'Er staat geen token in de URL.' });
        return;
      }

      setStatus({ fase: 'bezig' });
      // Een nieuwe launch: wat er van een vorige patiënt bewaard stond geldt niet meer,
      // ook niet als deze ruil mislukt.
      zetPatient(null);

      try {
        const antwoord = await api.post<SessieAntwoord>('/session', {
          appToken,
          accessToken,
          ...(externalUserId ? { externalUserId } : {}),
        });
        // De patiëntvelden gaan bij de oude weg niet naar de server: ze staan al in
        // de querystring, en de browser is de enige die ze nodig heeft.
        zetPatient(patientContext(patientGeslacht, patientTelefoon));
        verwerk(antwoord);
        schoonUrlOp();
      } catch (fout) {
        setStatus(naarStatus(fout));
      }
    },
    [accessToken, externalUserId, patientGeslacht, patientTelefoon, verwerk, zetPatient],
  );

  /**
   * De nieuwe manier van aanroepen: het HIS heeft de tokens al server-to-server
   * ingewisseld op POST /api/launch en gaf ons alleen een eenmalige code mee.
   *
   * De code werkt één keer en 60 seconden. Dat betekent ook dat een herlaadactie
   * hem niet nog eens kan gebruiken -- maar dat hoeft ook niet: op dat moment zit
   * de sessie in het cookie en komt `hervat` eraan te pas.
   */
  const wisselLaunchCode = useCallback(
    async (code: string) => {
      setStatus({ fase: 'bezig' });
      zetPatient(null);

      try {
        const antwoord = await api.post<SessieAntwoord>('/session', { launchCode: code });
        zetPatient(patientContext(antwoord.patient?.gender, antwoord.patient?.phone));
        verwerk(antwoord);
      } catch (fout) {
        setStatus(naarStatus(fout));
      } finally {
        // Ook na een mislukte poging: de code is dan opgebruikt of ongeldig, en
        // hij hoort hoe dan ook niet in de geschiedenis te blijven staan.
        schoonUrlOp();
      }
    },
    [verwerk, zetPatient],
  );

  /**
   * Verdergaan op het cookie, zonder tokens in de URL.
   *
   * Een lege querystring is geen afgewezen gebruiker. Wie vanuit dit scherm naar
   * /settings of /handleiding gaat en daar op terug drukt, komt terug op `/` zonder
   * query-parameters -- die staan daar met opzet niet meer in, want ze horen niet in
   * de browsergeschiedenis. Ook een herlaadactie op een schoongemaakte URL komt hier
   * uit. De sessie zit in dat geval gewoon in het HttpOnly-cookie, en GET /me geeft
   * hetzelfde antwoord als de tokenruil.
   */
  const hervat = useCallback(async () => {
    setStatus({ fase: 'bezig' });

    try {
      verwerk(await api.get<SessieAntwoord>('/me'));
    } catch (fout) {
      if (fout instanceof ApiFout && fout.status === 403) {
        setStatus({ fase: 'geblokkeerd', melding: fout.message });
        return;
      }
      // Ook bij een 401: er is geen sessie om op verder te gaan, en zonder token in
      // de URL is er ook niets om er een op te zetten.
      setStatus({ fase: 'geen-toegang', melding: 'Er staat geen token in de URL.' });
    }
  }, [verwerk]);

  useEffect(() => {
    if (alGewisseld.current) return;
    alGewisseld.current = true;

    // De launch-code gaat voor. Staat hij er, dan is de tokenruil al gedaan en
    // horen eventuele oude parameters in de URL genegeerd te worden.
    if (launchCode) {
      void wisselLaunchCode(launchCode);
      return;
    }

    if (!accessToken) {
      void hervat();
      return;
    }

    if (!appTokenUitUrl) {
      // Zonder app_token kan de uitwisseling niet; vraag erom in plaats van "geen
      // toegang" te tonen, want dit is een configuratiefout in het HIS en niet een
      // afgewezen gebruiker.
      setStatus({ fase: 'app-token-nodig' });
      return;
    }

    void wissel(appTokenUitUrl);
  }, [accessToken, appTokenUitUrl, launchCode, hervat, wissel, wisselLaunchCode]);

  const herlaad = useCallback(async () => {
    try {
      const antwoord = await api.get<SessieAntwoord>('/me');
      verwerk(antwoord);
    } catch (fout) {
      if (fout instanceof ApiFout && fout.status === 401) {
        setStatus({ fase: 'geen-toegang', melding: 'De sessie is verlopen.' });
      }
      // Andere fouten laten de bestaande gegevens staan: een mislukte verversing is
      // geen reden om een lopend consult af te breken.
    }
  }, [verwerk]);

  const pasSettingsAan = useCallback((deel: Partial<PracticeSettings>) => {
    setSettings((vorige) => ({ ...vorige, ...deel }));
  }, []);

  return {
    status,
    practice,
    practiceUserId,
    sip,
    settings,
    practiceIsNew,
    practiceNeedsName,
    needsOnboarding,
    patient,
    probeerAppToken: wissel,
    herlaad,
    pasSettingsAan,
    markeerOnboardingKlaar: useCallback(() => setNeedsOnboarding(false), []),
    markeerPraktijkNaam: useCallback((naam: string) => {
      setPractice((vorige) => (vorige ? { ...vorige, practiceName: naam } : vorige));
      setPracticeIsNew(false);
      setPracticeNeedsName(false);
    }, []),
  };
}
